import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import LoadingScreen from '../components/LoadingScreen';

export default function Completion({ userData, completionData, onReset }) {
  const [status, setStatus]     = useState('loading');
  const [result, setResult]     = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const hasFired = useRef(false);

  useEffect(() => {
    if (hasFired.current) return;
    hasFired.current = true;
    const process = async () => {
      try {
        const { data } = await axios.post('/api/complete-training', {
          userData,
          completedAt: completionData?.completedAt || new Date().toISOString(),
          signatureData: completionData?.signatureData || null,
          acknowledgedAt: completionData?.acknowledgedAt || null,
        });
        setResult(data);
        setStatus('done');
      } catch (err) {
        setErrorMsg(err.response?.data?.details || 'An error occurred while processing your certificate. Please contact your administrator.');
        setStatus('error');
      }
    };
    process();
  }, []); // eslint-disable-line

  const fmtDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' });
  };

  if (status === 'loading') return <div className="page-container"><LoadingScreen /></div>;

  if (status === 'error') return (
    <div className="page-container">
      <div className="card" style={{ textAlign: 'center', padding: '50px 40px' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <h2 style={{ color: '#1B3A5C', marginBottom: 12 }}>Processing Error</h2>
        <p style={{ color: '#546E7A', marginBottom: 8 }}>Your training has been recorded, but we encountered an issue generating your certificate:</p>
        <p style={{ background: '#FFF5F5', border: '1px solid #FEB2B2', borderRadius: 6, padding: '12px 16px', color: '#C53030', fontSize: 13, marginBottom: 28 }}>{errorMsg}</p>
        <p style={{ color: '#546E7A', fontSize: 13, marginBottom: 24 }}>Please contact your Krona Dentistry / Odyssey Solutions administrator and provide your name and completion date.</p>
        <button className="btn btn-outline" onClick={onReset}>Return to Start</button>
      </div>
    </div>
  );

  const certDate = fmtDate(result?.completionDate);

  return (
    <div className="page-container">
      <div style={{ marginBottom: 24 }}>
        <div className="step-badge"><span className="step-badge-dot" />Step 4 of 4 — Complete</div>
      </div>

      <div className="card">
        <div className="completion-hero">
          <div className="completion-checkmark">✓</div>
          <h1 className="completion-title">Training Complete!</h1>
          <p className="completion-subtitle">
            Congratulations, <strong>{userData?.fullName}</strong>.<br />
            You have successfully completed all required HIPAA Compliance Training modules.
          </p>
        </div>

        <div className="email-notice">
          <span className="email-notice-icon">📧</span>
          <div>
            <strong>Certificate emailed!</strong> Your signed certificate has been sent to <strong>{userData?.email}</strong>. Please check your inbox (and spam folder).
          </div>
        </div>

        <div className="cert-details-grid">
          {[
            { label: 'Employee Name',     value: userData?.fullName },
            { label: 'Job Role',          value: userData?.role },
            { label: 'Date Completed',    value: certDate },
            { label: 'Certificate ID',    value: result?.certificateId },
            { label: 'Email',             value: userData?.email },
            { label: 'Modules Completed', value: '6 of 6  ✓' },
          ].map(({ label, value }) => (
            <div key={label} className="cert-detail-item">
              <div className="cert-detail-label">{label}</div>
              <div className="cert-detail-value">{value || '—'}</div>
            </div>
          ))}
        </div>

        {result?.driveLink && (
          <div style={{ background: '#EEF3F8', border: '1px solid #CFD8DC', borderRadius: 8, padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
            <span style={{ fontSize: 20 }}>☁️</span>
            <div>
              <strong style={{ color: '#1B3A5C' }}>Stored in Google Drive</strong><br />
              <a href={result.driveLink} target="_blank" rel="noreferrer" style={{ color: '#00A3A3', wordBreak: 'break-all' }}>{result.driveLink}</a>
            </div>
          </div>
        )}

        <div className="cert-actions">
          <button className="btn btn-primary btn--lg" onClick={async () => {
            try {
              const resp = await axios.post('/api/download-certificate', {
                userData,
                completedAt: result?.completionDate,
                certificateId: result?.certificateId,
              }, { responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
              const a = document.createElement('a');
              a.href = url;
              a.download = `HIPAA-Certificate-${userData?.fullName?.replace(/\s+/g, '-') || 'Certificate'}.pdf`;
              a.click();
              window.URL.revokeObjectURL(url);
            } catch (e) {
              alert('Could not download certificate. Please check your email.');
            }
          }}>⬇️ Download Certificate</button>
          {result?.driveLink && (
            <a href={result.driveLink} target="_blank" rel="noreferrer" className="btn btn-secondary btn--lg" style={{ textDecoration: 'none' }}>☁️ View in Drive</a>
          )}
          <button className="btn btn-outline" onClick={onReset} style={{ marginLeft: 'auto' }}>Start New Session</button>
        </div>
      </div>

      <div style={{ marginTop: 24, padding: '20px 28px', background: '#fff', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderLeft: '4px solid #00A3A3' }}>
        <p style={{ fontSize: 14, color: '#2C3E50', lineHeight: 1.7 }}>
          <strong>Thank you for completing your HIPAA training.</strong> Your completion has been recorded in the Krona Dentistry / Odyssey Solutions compliance tracking system.
        </p>
        <p style={{ fontSize: 13, color: '#546E7A', marginTop: 10, fontStyle: 'italic' }}>
          — Krona Dentistry / Odyssey Solutions, HIPAA Compliance Training Program
        </p>
      </div>
    </div>
  );
}
